// AppShell — the authenticated layout: a sidebar with the navigation grouped
// by what you're doing (FD §22.1), and an <Outlet/> for the active route.
// Mounted as the pathless parent route for every page except /login (see
// router.tsx). Below the lg breakpoint the sidebar becomes a top bar with a
// menu sheet.

import { useEffect, useState } from 'react'
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { approvalsApi, boardApi, meApi } from '../lib/api-client'
import { useAuth } from '../lib/auth'
import { humanize } from '../lib/format'
import { ProjectProvider, useCurrentProject } from '../lib/project-context'
import { ChatPanel } from './chat/chat-panel'
import { ProjectSwitcher } from './project-switcher'
import { WorkspaceSwitcher } from './workspace-switcher'
import { STREAM_COLOR_CLASS } from './board/board-utils'

type NavPath =
  | '/dashboard'
  | '/wall'
  | '/approvals'
  | '/chat'
  | '/knowledge'
  | '/roles'
  | '/skills'
  | '/executors'
  | '/mcp-services'
  | '/credentials'
  | '/audit'
  | '/settings'
  | '/installation/sandbox-hosts'

type ProjectNavPath =
  | '/p/$projectKey/overview'
  | '/p/$projectKey/tasks'
  | '/p/$projectKey/agents'
  | '/p/$projectKey/runs'
  | '/p/$projectKey/reports'
  | '/p/$projectKey/knowledge'
  | '/p/$projectKey/settings'

/**
 * `pageContext` (07-chat.md §2.4) the chat panel sends with a message, so
 * "this workflow"/"this project" resolves — derived from the current
 * pathname rather than plumbed down from every route. On /p/:key/... routes
 * it is the current project.
 */
function pageContextFor(
  pathname: string,
  projectId: string | undefined
): Record<string, unknown> | undefined {
  const workflow = /^\/projects\/([^/]+)\/workflows\/([^/]+)\/edit$/.exec(pathname)
  if (workflow) return { projectId: workflow[1], workflowKey: workflow[2] }
  if (/^\/p\/[^/]+/.test(pathname)) return projectId ? { projectId } : undefined
  const project = /^\/projects\/([^/]+)/.exec(pathname)
  if (project) return { projectId: project[1] }
  const run = /^\/runs\/([^/]+)/.exec(pathname)
  if (run) return { runId: run[1] }
  return undefined
}

interface NavItem {
  to: NavPath
  label: string
  showApprovalsBadge?: boolean
}

const PROJECT_ITEMS: { to: ProjectNavPath; label: string }[] = [
  { to: '/p/$projectKey/overview', label: 'Overview' },
]
const PROJECT_ITEMS_AFTER_BOARDS: { to: ProjectNavPath; label: string }[] = [
  { to: '/p/$projectKey/tasks', label: 'Tasks' },
  { to: '/p/$projectKey/agents', label: 'Agents' },
  { to: '/p/$projectKey/runs', label: 'Runs' },
  { to: '/p/$projectKey/reports', label: 'Reports' },
  { to: '/p/$projectKey/knowledge', label: 'Knowledge' },
  { to: '/p/$projectKey/settings', label: 'Project settings' },
]

/** Everything outside the current project, in small groups so the list stays scannable. */
const WORKSPACE_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Work',
    items: [
      { to: '/approvals', label: 'Desk', showApprovalsBadge: true },
      { to: '/chat', label: 'Assistant' },
      { to: '/dashboard', label: 'All projects' },
      { to: '/wall', label: 'Wall display' },
    ],
  },
  {
    label: 'Library',
    items: [
      { to: '/roles', label: 'Roles' },
      { to: '/skills', label: 'Skills' },
      { to: '/knowledge', label: 'Knowledge base' },
    ],
  },
  {
    label: 'Connections',
    items: [
      { to: '/executors', label: 'Executors' },
      { to: '/mcp-services', label: 'MCP servers' },
      { to: '/credentials', label: 'Credentials' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { to: '/audit', label: 'Audit log' },
      { to: '/settings', label: 'Settings' },
    ],
  },
]

/**
 * Installation-wide settings, not one workspace's: only shown to installation
 * administrators (sandbox-hosts ontwerp §11; `GET /me` decides, §15.1 item 10).
 */
const INSTALLATION_GROUP: { label: string; items: NavItem[] } = {
  label: 'Installation',
  items: [{ to: '/installation/sandbox-hosts', label: 'Sandbox hosts' }],
}

const NAV_LINK_CLASS =
  'group flex items-center justify-between rounded-control px-2.5 py-1.5 text-base font-medium text-ink-2 transition-colors hover:bg-sunken hover:text-ink'
const NAV_ACTIVE_PROPS = {
  className: '!bg-moss-soft !text-moss-strong',
  'aria-current': 'page' as const,
}

export function AppShell() {
  return (
    <ProjectProvider>
      <ShellLayout />
    </ProjectProvider>
  )
}

function ShellLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const workspaceId = user?.workspaceId ?? ''
  const { project } = useCurrentProject()
  const [boardsOpen, setBoardsOpen] = useState(true)
  // The workflow editor is a canvas tool: it wants the full viewport, not
  // the standard max-width reading column (workflow-editor.tsx manages its
  // own scrolling/height). The chat panel lays over it rather than sharing
  // the row, same as it does on mobile (07-chat.md §2.8).
  // The chat pages manage their own two-pane scrolling too, same reasoning.
  // The board uses the whole width too (its columns scroll sideways).
  const isFullBleed =
    /\/workflows\/[^/]+\/edit$/.test(pathname) ||
    /^\/chat(\/|$)/.test(pathname) ||
    /^\/p\/[^/]+\/boards\/[^/]+/.test(pathname)
  const chatOverlay = isFullBleed

  // Close the mobile menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [pathname])

  // Cmd/Ctrl+. toggles the assistant panel from anywhere in the app.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key === '.') {
        event.preventDefault()
        setChatOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const pendingApprovalsQuery = useQuery({
    queryKey: ['approvals', workspaceId, 'pending'],
    queryFn: () => approvalsApi.list({ workspaceId, status: 'pending' }),
    enabled: workspaceId !== '',
    refetchInterval: 30_000,
  })
  const pendingApprovalsCount = pendingApprovalsQuery.data?.items.length ?? 0

  // One line per stream under Boards, with its open count.
  const overviewQuery = useQuery({
    queryKey: ['project', project?.id ?? '', 'overview'],
    queryFn: () => boardApi.overview(project!.id),
    enabled: project != null,
    refetchInterval: 30_000,
  })
  const streams = overviewQuery.data?.streams ?? []

  // Fresh from the API rather than the token's claim, so an administrator
  // sees the group right after a deploy without signing in again.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    enabled: user != null,
    staleTime: 60_000,
  })
  const navGroups = meQuery.data?.isInstanceAdmin
    ? [...WORKSPACE_GROUPS, INSTALLATION_GROUP]
    : WORKSPACE_GROUPS

  function handleLogout(): void {
    logout()
    void navigate({ to: '/login' })
  }

  const who =
    user?.email ?? (user?.roles[0] ? humanize(user.roles[0]) : 'Signed in')

  const projectKey = project?.key ?? ''
  const nav = (
    <nav aria-label="Main" className="flex flex-1 flex-col overflow-y-auto pb-4">
      <div className="pt-3">
        <ProjectSwitcher />
      </div>
      <div className="px-3 pt-3">
        {project ? (
          <div className="mb-5">
            <p className="px-2.5 pb-1.5 text-sm text-ink-3">Project</p>
            <ul className="space-y-px">
              {PROJECT_ITEMS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    params={{ projectKey }}
                    className={NAV_LINK_CLASS}
                    activeProps={NAV_ACTIVE_PROPS}
                  >
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
              <li>
                <div className="flex items-center">
                  <Link
                    to="/p/$projectKey/boards"
                    params={{ projectKey }}
                    className={`${NAV_LINK_CLASS} flex-1`}
                    activeProps={{ 'aria-current': 'page' as const }}
                    activeOptions={{ exact: false }}
                  >
                    <span>Boards</span>
                  </Link>
                  {streams.length > 0 ? (
                    <button
                      type="button"
                      className="btn-ghost btn-sm shrink-0"
                      aria-expanded={boardsOpen}
                      aria-label={boardsOpen ? 'Collapse boards' : 'Expand boards'}
                      onClick={() => setBoardsOpen((open) => !open)}
                    >
                      {boardsOpen ? '▾' : '▸'}
                    </button>
                  ) : null}
                </div>
                {boardsOpen && streams.length > 0 ? (
                  <ul className="mb-1 ml-3 space-y-px border-l border-line pl-2">
                    {streams.map(({ stream, openCount }) => (
                      <li key={stream.key}>
                        <Link
                          to="/p/$projectKey/boards/$streamKey"
                          params={{ projectKey, streamKey: stream.key }}
                          className="group flex items-center justify-between gap-2 rounded-control px-2 py-1 text-base text-ink-2 transition-colors hover:bg-sunken hover:text-ink"
                          activeProps={NAV_ACTIVE_PROPS}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <span
                              aria-hidden
                              className={`h-2 w-2 shrink-0 rounded-full ${STREAM_COLOR_CLASS[stream.color].dot}`}
                            />
                            <span className="truncate">{stream.name}</span>
                          </span>
                          <span className="text-sm tabular-nums text-ink-3">{openCount}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
              {PROJECT_ITEMS_AFTER_BOARDS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    params={{ projectKey }}
                    className={NAV_LINK_CLASS}
                    activeProps={NAV_ACTIVE_PROPS}
                  >
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="space-y-4">
          {navGroups.map((group) => (
            <div key={group.label}>
              <p className="px-2.5 pb-1.5 text-sm text-ink-3">{group.label}</p>
              <ul className="space-y-px">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={NAV_LINK_CLASS} activeProps={NAV_ACTIVE_PROPS}>
                      <span>{item.label}</span>
                      {item.showApprovalsBadge && pendingApprovalsCount > 0 ? (
                        <span
                          className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-signal px-1.5 text-xs font-semibold leading-5 text-surface"
                          aria-label={`${pendingApprovalsCount} waiting`}
                        >
                          {pendingApprovalsCount}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </nav>
  )

  const brand = <WorkspaceSwitcher />

  const account = (
    <div className="border-t border-line px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-sunken text-sm font-semibold text-ink-2"
          >
            {who.slice(0, 1).toUpperCase()}
          </span>
          <span className="min-w-0 text-sm leading-tight text-ink-2">
            {who}
          </span>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="btn-ghost btn-sm -mr-2 shrink-0"
        >
          Sign out
        </button>
      </div>
    </div>
  )

  // Mobile: always an overlay layer (its own backdrop, fixed to the
  // viewport). Desktop: a real column next to <Outlet/>, except in the
  // full-bleed workflow editor, which wants the whole row to itself — 07
  // §2.8's "als laag over de pagina".
  const chatPanelClasses = chatOverlay
    ? 'fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col border-l border-line bg-surface shadow-overlay'
    : 'fixed inset-y-0 right-0 z-30 flex w-full max-w-md flex-col border-l border-line bg-surface shadow-overlay lg:static lg:z-auto lg:w-96 lg:shrink-0 lg:shadow-none'

  return (
    <div className="flex h-screen flex-col bg-canvas text-ink lg:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-surface lg:flex">
        <div className="px-5 pb-2 pt-5">{brand}</div>
        {nav}
        <div className="border-t border-line px-3 py-3">
          <button
            type="button"
            className="btn-secondary btn-sm w-full justify-between"
            aria-expanded={chatOpen}
            onClick={() => setChatOpen((open) => !open)}
          >
            Assistant
            <span className="text-sm text-ink-3">⌘.</span>
          </button>
        </div>
        {account}
      </aside>

      {/* Mobile top bar + sheet */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface px-4 lg:hidden">
        {brand}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            aria-expanded={chatOpen}
            aria-label="Toggle assistant panel"
            onClick={() => setChatOpen((open) => !open)}
          >
            Assistant
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'Close' : 'Menu'}
            {!menuOpen && pendingApprovalsCount > 0 ? (
              <span
                className="h-2 w-2 rounded-full bg-signal"
                aria-label="Approvals waiting"
              />
            ) : null}
          </button>
        </div>
      </header>
      {menuOpen ? (
        <div className="fixed inset-x-0 bottom-0 top-14 z-20 flex flex-col bg-surface lg:hidden">
          {nav}
          {account}
        </div>
      ) : null}

      <main
        className={`min-w-0 flex-1 ${isFullBleed ? 'overflow-hidden' : 'overflow-y-auto'}`}
      >
        {isFullBleed ? (
          <Outlet />
        ) : (
          <div className="mx-auto w-full max-w-[1180px] px-4 py-6 sm:px-8 sm:py-9">
            <Outlet />
          </div>
        )}
      </main>

      {chatOpen ? (
        <>
          <div
            className={`fixed inset-0 z-20 bg-ink/25 ${chatOverlay ? '' : 'lg:hidden'}`}
            onClick={() => setChatOpen(false)}
            aria-hidden
          />
          <div className={chatPanelClasses}>
            <ChatPanel
              onClose={() => setChatOpen(false)}
              pageContext={pageContextFor(pathname, project?.id)}
            />
          </div>
        </>
      ) : null}
    </div>
  )
}
